import type { ToolDefinition } from "./types";

export const tools: ToolDefinition[] = [
  {
    slug: "fps-payment-profile-builder",
    name: "FPS Payment Profile Builder",
    shortName: "Payment profile builder",
    category: "FPS & Hong Kong Business",
    description: "Build a clear business payment profile with FPS receiving details, recipient name, and payer instructions.",
    eyebrow: "Free FPS business tool",
    privacyNote: "Your draft stays in this browser until you choose to publish it.",
    steps: [
      "Add the business and FPS recipient details customers should verify.",
      "Review the live payment-page preview for clarity.",
      "Continue to the Lucaden dashboard to publish your stable payment URL."
    ],
    faq: [
      {
        question: "Does Lucaden receive the payment?",
        answer: "No. The payer completes the transfer in their own bank or wallet app, directly to the FPS details shown by the business."
      },
      {
        question: "Can I edit the profile later?",
        answer: "Yes. After publishing, the business can update its profile from the dashboard while keeping the same public URL."
      }
    ]
  },
  {
    slug: "fps-payment-page-qr-generator",
    name: "FPS Payment Page QR Generator",
    shortName: "Payment page QR generator",
    category: "FPS & Hong Kong Business",
    description: "Turn a Lucaden FPS payment profile URL into a downloadable QR code for invoices, signs, and messages.",
    eyebrow: "Free local QR tool",
    privacyNote: "The QR is generated in your browser; the profile URL is not uploaded by this tool.",
    steps: [
      "Paste the public Lucaden payment profile URL.",
      "Check the destination shown beside the live QR preview.",
      "Download the PNG and place it on a message, invoice, or printed sign."
    ],
    faq: [
      {
        question: "Does scanning the QR send money?",
        answer: "No. It opens the payment profile so the payer can verify the recipient details before using their bank or wallet app."
      },
      {
        question: "Will the QR still work after I edit my profile?",
        answer: "Yes, provided the public profile URL stays the same. The QR points to the URL, not to a frozen copy of the page."
      }
    ]
  },
  {
    slug: "fps-dynamic-payment-request-link-generator",
    name: "FPS Dynamic Payment Request Link Generator",
    shortName: "Dynamic payment request link",
    category: "FPS & Hong Kong Business",
    description: "Prepare an FPS request with an amount, purpose, reference, customer, and expiry context, then save its shareable link.",
    eyebrow: "Free FPS request tool",
    privacyNote: "The draft stays in this browser until you continue and save the request to your Lucaden account.",
    steps: [
      "Enter the amount and the context the payer should see.",
      "Review the request summary and reference for mistakes.",
      "Continue to save the request and create its unique shareable URL."
    ],
    faq: [
      {
        question: "Why must the request be saved before I get a link?",
        answer: "The unique URL needs a stored request record so it can keep showing the correct amount, reference, and expiry later."
      },
      {
        question: "Does a page view confirm payment?",
        answer: "No. The merchant must verify receipt in their own bank or wallet account before marking a request as paid."
      }
    ]
  },
  {
    slug: "fps-payment-and-invoice-record-creator",
    name: "FPS Payment and Invoice Record Creator",
    shortName: "Payment and invoice record",
    category: "FPS & Hong Kong Business",
    description: "Create an invoice-style FPS request record and continue to its saved, shareable payment link.",
    eyebrow: "Free FPS record tool",
    privacyNote: "The draft stays in this browser until you continue and save it to your Lucaden account.",
    steps: [
      "Add the invoice number, purpose, amount, customer, and due date.",
      "Review the invoice-style summary before saving.",
      "Continue to the dashboard to save the record and create its shareable payment link."
    ],
    faq: [
      {
        question: "Is this a bank-issued invoice?",
        answer: "No. It is a Lucaden workflow record that connects invoice context to an FPS payment request page."
      },
      {
        question: "Does the record update automatically when payment arrives?",
        answer: "No. The merchant checks their own receiving account and then marks the record as paid."
      }
    ]
  },
  {
    slug: "isbn-barcode-generator",
    name: "ISBN Barcode Generator",
    shortName: "ISBN barcode generator",
    category: "QR & Barcode",
    description: "Validate an ISBN-10 or ISBN-13 and render its corresponding EAN-13 book barcode.",
    eyebrow: "Free book barcode tool",
    privacyNote: "ISBN validation and barcode rendering happen entirely in your browser.",
    steps: [
      "Enter the ISBN printed in the book metadata.",
      "Let the tool verify its check digit and convert ISBN-10 to ISBN-13 when needed.",
      "Download the EAN-13 barcode PNG and scan-test it before production use."
    ],
    faq: [
      {
        question: "Does this tool assign a new ISBN?",
        answer: "No. It only validates and renders an ISBN you already have. Obtain ISBN assignments from the appropriate ISBN agency."
      },
      {
        question: "Why is an ISBN-10 converted?",
        answer: "Retail book barcodes use the 13-digit Bookland EAN form, so a valid ISBN-10 is converted to its 978-prefixed ISBN-13 equivalent."
      }
    ]
  },
  {
    slug: "qr-code-generator",
    name: "QR Code Generator",
    shortName: "QR code generator",
    category: "QR & Barcode",
    description: "Generate downloadable QR codes for text or URLs with size and error-correction controls.",
    eyebrow: "Free local QR tool",
    privacyNote: "The entered content is encoded locally and is never uploaded to Lucaden.",
    steps: [
      "Enter the text, URL, or short information you want to encode.",
      "Choose an image size and the error-correction level.",
      "Download the PNG and scan-test the final image before sharing it."
    ],
    faq: [
      {
        question: "What does error correction do?",
        answer: "Higher levels add redundancy so a QR may remain readable after minor damage, but they also make the code denser."
      },
      {
        question: "Does Lucaden save my QR content?",
        answer: "No. QR generation happens in the browser and this tool does not transmit or retain the entered content."
      }
    ]
  },
  {
    slug: "qr-code-scanner",
    name: "QR Code Scanner",
    shortName: "QR code scanner",
    category: "QR & Barcode",
    description: "Scan QR codes from an uploaded image or camera and preview the decoded content safely.",
    eyebrow: "Free local QR scanner",
    privacyNote: "Uploaded images and camera frames are decoded locally and are not sent to Lucaden.",
    steps: [
      "Upload a clear image or grant camera access.",
      "Place the complete QR code inside the preview area.",
      "Review decoded content and inspect any destination before choosing to open it."
    ],
    faq: [
      {
        question: "Will the scanner automatically open a link?",
        answer: "No. It shows the decoded content and destination host first, leaving the decision to open it with you."
      },
      {
        question: "Why can a scan fail?",
        answer: "Blur, glare, low contrast, missing corners, or a QR that is too small in the image can prevent reliable decoding."
      }
    ]
  },
  {
    slug: "barcode-generator",
    name: "Barcode Generator",
    shortName: "Barcode generator",
    category: "QR & Barcode",
    description: "Generate validated Code 128, EAN-13, UPC-A, and Code 39 barcodes as downloadable PNG images.",
    eyebrow: "Free 1D barcode tool",
    privacyNote: "Barcode values are validated and rendered locally in your browser.",
    steps: [
      "Choose the barcode standard required by the receiving system.",
      "Enter the identifier and review any calculated check digit.",
      "Download and scan-test the PNG without stretching or cropping its quiet zone."
    ],
    faq: [
      {
        question: "Does generating a barcode register a product number?",
        answer: "No. Use identifiers assigned by the relevant standards body or your authorized internal system."
      },
      {
        question: "Which barcode should I choose?",
        answer: "EAN-13 and UPC-A are common in retail, Code 128 supports dense general identifiers, and Code 39 is common in some industrial workflows."
      }
    ]
  },
  {
    slug: "barcode-scanner",
    name: "Barcode Scanner",
    shortName: "Barcode scanner",
    category: "QR & Barcode",
    description: "Decode supported one-dimensional and two-dimensional barcodes from a camera or uploaded image.",
    eyebrow: "Free local barcode scanner",
    privacyNote: "Images and camera frames are decoded locally and are not uploaded to Lucaden.",
    steps: [
      "Upload a sharp image or start the device camera.",
      "Keep the full barcode and its quiet zone visible with minimal glare.",
      "Copy the decoded value after checking the detected barcode format."
    ],
    faq: [
      {
        question: "Which formats can this scanner decode?",
        answer: "It supports common formats including Code 128, EAN, UPC, Code 39, QR, Data Matrix, Aztec, and PDF417 where image quality allows."
      },
      {
        question: "Are uploaded barcode images stored?",
        answer: "No. The browser creates a temporary local image URL for decoding and releases it when you leave the tool."
      }
    ]
  },
  {
    slug: "wifi-qr-code-generator",
    name: "WiFi QR Code Generator",
    shortName: "WiFi QR generator",
    category: "QR & Barcode",
    description: "Create a local QR code containing a Wi-Fi network name, security setting, and optional password.",
    eyebrow: "Private Wi-Fi QR tool",
    privacyNote: "The network name and password never leave your browser.",
    steps: [
      "Enter the network name and select its security type.",
      "Add the password and hidden-network setting when applicable.",
      "Download the QR and test it with a device before displaying it for guests."
    ],
    faq: [
      {
        question: "Can anyone who sees the QR join the network?",
        answer: "Potentially yes. Treat the QR like the Wi-Fi password and share it only where intended."
      },
      {
        question: "Is the Wi-Fi password sent to Lucaden?",
        answer: "No. The Wi-Fi payload and QR image are generated locally in the browser."
      }
    ]
  },
  {
    slug: "smart-invoice-workspace",
    name: "Smart Invoice Workspace",
    shortName: "Smart invoice workspace",
    category: "Invoices & Operations",
    description: "Create, export, share, and track professional invoices with clients, payment links, signatures, recurring schedules, and follow-up reminders.",
    eyebrow: "Free invoice operations tool",
    privacyNote: "Guest invoices stay in this browser. Sign in only when you want secure cloud storage, hosted invoice links, view tracking, signatures, and client portals.",
    steps: [
      "Choose one of ten designs and add the parties, line items, tax, discounts, payment options, and agreement details.",
      "Review the live invoice, export a multilingual PDF, and save it locally or to your signed-in workspace.",
      "Share the hosted invoice, track its status and views, manage client portals, and prepare overdue follow-ups."
    ],
    faq: [
      {
        question: "Can I use the invoice tool without an account?",
        answer: "Yes. Creation, local drafts, client records, settings, QR payment options, and PDF export work in guest mode. Browser storage can be cleared, so download important invoices or sign in for cloud storage."
      },
      {
        question: "Does Lucaden process invoice payments?",
        answer: "No. You can add your own HTTPS payment destinations, such as an FPS page, bank page, PayPal, Wise, or Stripe Payment Link. Payment happens with that provider, and you record the result in the workspace."
      },
      {
        question: "What happens when a client signs an agreement?",
        answer: "A hosted invoice records the signer name and timestamp, then unlocks its payment options. This workflow is a practical acceptance record, not a substitute for jurisdiction-specific legal advice."
      }
    ]
  }
];

export function getToolBySlug(slug: string) {
  return tools.find((tool) => tool.slug === slug);
}
